import { useState, useRef, useEffect } from 'react';
import { userFeedbackManager, buildMailtoUrl, type UserFeedback } from '@/utils/userFeedback';

type FeedbackType = UserFeedback['type'];
type Priority = UserFeedback['priority'];

const FEEDBACK_TYPES: { value: FeedbackType; label: string }[] = [
  { value: 'bug_report', label: 'Bug Report' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'general_feedback', label: 'General Feedback' },
  { value: 'usability_issue', label: 'Usability Issue' },
  { value: 'performance_issue', label: 'Performance Issue' },
];

const PRIORITIES: { value: Priority; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const CATEGORIES = [
  'general',
  'calculator',
  'optimization',
  'cost_estimation',
  'accessibility',
  'other',
];

interface FeedbackFormProps {
  open: boolean;
  onClose: () => void;
}

export function FeedbackForm({ open, onClose }: FeedbackFormProps) {
  const [type, setType] = useState<FeedbackType>('general_feedback');
  const [priority, setPriority] = useState<Priority>('medium');
  const [category, setCategory] = useState('general');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [feedbackId, setFeedbackId] = useState('');
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (!dialogRef.current) return;
    if (open && !dialogRef.current.open) dialogRef.current.showModal();
    if (!open && dialogRef.current.open) dialogRef.current.close();
  }, [open]);

  const reset = () => {
    setType('general_feedback');
    setPriority('medium');
    setCategory('general');
    setTitle('');
    setDescription('');
    setSubmitted(false);
    setFeedbackId('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;

    const id = userFeedbackManager.submitFeedback({
      type,
      priority,
      category,
      title: title.trim(),
      description: description.trim(),
      url: window.location.href,
    });
    setFeedbackId(id);
    setSubmitted(true);
  };

  const handleMailto = () => {
    const url = buildMailtoUrl({ type, priority, category, title, description, url: window.location.href });
    if (url) window.open(url, '_blank');
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-2) var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg-primary)',
    color: 'var(--color-text-primary)',
    fontSize: 'var(--font-size-base)',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    marginBottom: 'var(--space-1)',
    color: 'var(--color-text-secondary)',
  };

  return (
    <dialog
      ref={dialogRef}
      onClose={handleClose}
      onClick={(e) => { if (e.target === dialogRef.current) handleClose(); }}
      style={{
        border: 'none',
        borderRadius: 'var(--radius-xl)',
        padding: 0,
        maxWidth: 480,
        width: '90vw',
        background: 'var(--color-bg-card)',
        color: 'var(--color-text-primary)',
        boxShadow: 'var(--shadow-lg)',
      }}
    >
      <div style={{ padding: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 'var(--font-weight-bold)', margin: 0 }}>
            Send Feedback
          </h2>
          <button type="button" className="btn btn-sm" onClick={handleClose} aria-label="Close">
            &times;
          </button>
        </div>

        {submitted ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-4) 0' }}>
            <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-medium)', marginBottom: 'var(--space-2)' }}>
              Thank you for your feedback!
            </p>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginBottom: 'var(--space-4)' }}>
              ID: {feedbackId}
            </p>
            {import.meta.env.VITE_FEEDBACK_EMAIL && (
              <button type="button" className="btn btn-primary" onClick={handleMailto} style={{ marginBottom: 'var(--space-2)' }}>
                Send via Email
              </button>
            )}
            <br />
            <button type="button" className="btn" onClick={handleClose}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 'var(--space-3)' }}>
              <label htmlFor="feedback-type" style={labelStyle}>Type</label>
              <select id="feedback-type" value={type} onChange={(e) => setType(e.target.value as FeedbackType)} style={inputStyle}>
                {FEEDBACK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
              <div>
                <label htmlFor="feedback-priority" style={labelStyle}>Priority</label>
                <select id="feedback-priority" value={priority} onChange={(e) => setPriority(e.target.value as Priority)} style={inputStyle}>
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="feedback-category" style={labelStyle}>Category</label>
                <select id="feedback-category" value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 'var(--space-3)' }}>
              <label htmlFor="feedback-title" style={labelStyle}>Title</label>
              <input id="feedback-title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Brief summary" style={inputStyle} required />
            </div>

            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label htmlFor="feedback-description" style={labelStyle}>Description</label>
              <textarea
                id="feedback-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue or suggestion in detail..."
                rows={4}
                required
                style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
              />
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <button type="button" className="btn" onClick={handleClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={!title.trim() || !description.trim()}>
                Submit
              </button>
            </div>
          </form>
        )}
      </div>
    </dialog>
  );
}
